// The words the style picker shows for each S&S style (designer and Studio): a one-line description and the
// category chip it sits under. Styles list in this table's order within a category. A style imported before it is
// added here still shows, under the category its garment type suggests, with no description.

export const CATEGORIES = ["tees", "performance", "polos", "long-sleeve", "fleece", "tanks", "youth", "pants", "hats", "totes", "other"] as const;
export type Category = (typeof CATEGORIES)[number];

export const CATEGORY_LABEL: Record<Category, string> = {
  tees: "T-shirts",
  performance: "Performance tees",
  polos: "Polos",
  "long-sleeve": "Long sleeve",
  fleece: "Hoodies and sweatshirts",
  tanks: "Tanks",
  youth: "Youth and baby",
  pants: "Joggers and sweats",
  hats: "Hats",
  totes: "Totes",
  // Hand-registered garments (no S&S style): the Studio only.
  other: "Other",
};

export interface StyleCopy {
  description: string;
  category: Category;
}

// An array, not an object: numeric keys like "3001" would lose their order in one.
const STYLES: [string, StyleCopy][] = [
  ["3001", { description: "Soft, fitted unisex tee", category: "tees" }],
  ["64000", { description: "Light, soft everyday tee", category: "tees" }],
  ["5000", { description: "Classic heavy cotton tee", category: "tees" }],
  ["1717", { description: "Garment-dyed heavyweight tee with a worn-in look", category: "tees" }],
  ["3001CVC", { description: "Heathered blend of the soft, fitted 3001", category: "tees" }],
  ["3413", { description: "Triblend tee, extra soft and drapey", category: "tees" }],
  ["6400", { description: "Women's relaxed fit tee", category: "tees" }],
  ["3600", { description: "Smooth, fitted cotton tee", category: "tees" }],
  ["6210", { description: "Soft cotton blend, fitted", category: "tees" }],
  ["2000", { description: "Thick, durable cotton tee", category: "tees" }],
  ["8000", { description: "Cotton blend that holds its shape", category: "tees" }],
  ["6030", { description: "Garment-dyed pocket tee, left chest and back prints", category: "tees" }],
  ["6004", { description: "Women's slim fit tee", category: "tees" }],
  ["42000", { description: "Lightweight polyester tee that wicks sweat", category: "performance" }],
  ["4120", { description: "Polyester athletic tee that wicks sweat", category: "performance" }],
  ["M348", { description: "Snag-resistant polyester polo, left chest and back prints", category: "polos" }],
  ["6014", { description: "Garment-dyed heavyweight long sleeve tee", category: "long-sleeve" }],
  ["5400", { description: "Classic heavy cotton long sleeve tee", category: "long-sleeve" }],
  ["3501", { description: "Soft, fitted long sleeve tee", category: "long-sleeve" }],
  ["3200", { description: "Three-quarter sleeve raglan baseball tee", category: "long-sleeve" }],
  ["18500", { description: "Classic pullover hoodie", category: "fleece" }],
  ["SS4500", { description: "Midweight pullover hoodie", category: "fleece" }],
  ["S700", { description: "Thick, soft pullover hoodie", category: "fleece" }],
  ["SS4500Z", { description: "Midweight full-zip hoodie, left chest and back prints", category: "fleece" }],
  ["SS4600QZ", { description: "Midweight quarter-zip pullover, left chest and back prints", category: "fleece" }],
  ["18000", { description: "Classic crewneck sweatshirt", category: "fleece" }],
  ["3480", { description: "Lightweight jersey tank", category: "tanks" }],
  ["5000B", { description: "Youth classic cotton tee", category: "youth" }],
  ["18500B", { description: "Youth pullover hoodie", category: "youth" }],
  ["18000B", { description: "Youth crewneck sweatshirt", category: "youth" }],
  ["4400", { description: "Infant rib bodysuit, small front print", category: "youth" }],
  ["IND20PNT", { description: "Midweight fleece sweatpants, print on the left hip", category: "pants" }],
  // Six-panel caps have a seam down the middle of the front, five-panel caps a seamless front panel: buyers pick by it.
  ["112", { description: "Snapback trucker cap, six-panel, center seam, mesh back", category: "hats" }],
  ["5511UP", { description: "Fitted trucker cap, five-panel, seamless front, mesh back", category: "hats" }],
  ["110M5", { description: "Adjustable mesh-back cap, five-panel", category: "hats" }],
  ["EC7094", { description: "Trucker cap, five-panel, lower crown", category: "hats" }],
  ["5054", { description: "Premium rope cap, five-panel, lower crown", category: "hats" }],
  ["172", { description: "Fitted sport mesh cap, six-panel, center seam", category: "hats" }],
  ["X210SM", { description: "Adjustable sport mesh cap, six-panel, center seam", category: "hats" }],
  ["Q800", { description: "Cotton canvas tote bag", category: "totes" }],
];
export const STYLE_COPY: ReadonlyMap<string, StyleCopy> = new Map(STYLES);

const TYPE_CATEGORY: Record<string, Category> = { tee: "tees", sunshirt: "long-sleeve", hoodie: "fleece", crewneck: "fleece", tote: "totes", hat: "hats" };

// A style's copy; a style missing from the table goes under its garment type's category with no description, and
// hand-registered garments (no style) under Other.
export function styleCopy(style: string | null, type: string): StyleCopy {
  if (style === null) return { description: "", category: "other" };
  return STYLE_COPY.get(style) ?? { description: "", category: TYPE_CATEGORY[type] ?? "tees" };
}

// Where a style sorts within its category: table order first, then the rest by name.
export function styleRank(style: string | null): number {
  const i = style === null ? -1 : STYLES.findIndex(([key]) => key === style);
  return i === -1 ? Number.MAX_SAFE_INTEGER : i;
}
